import type { SeverityLevel, EventType, EventStatus } from '../redux/api/eventsApi';
import type { TaskStatus } from '../redux/api/tasksApi';

export const SEVERITY_HEX_COLORS: Record<SeverityLevel, string> = {
  critical: '#EF4444',
  high: '#F97316',
  medium: '#F59E0B',
  low: '#84CC16',
};

export const SEVERITY_PANEL_COLORS: Record<SeverityLevel, { bg: string; text: string }> = {
  critical: { bg: 'bg-red-100 dark:bg-red-950/40', text: 'text-red-700 dark:text-red-400' },
  high: { bg: 'bg-orange-100 dark:bg-orange-950/40', text: 'text-orange-700 dark:text-orange-400' },
  medium: { bg: 'bg-yellow-100 dark:bg-yellow-950/40', text: 'text-yellow-700 dark:text-yellow-400' },
  low: { bg: 'bg-green-100 dark:bg-green-950/40', text: 'text-green-700 dark:text-green-400' },
};

export const SEVERITY_CARD_COLORS: Record<SeverityLevel, { bg: string; text: string; border: string }> = {
  critical: { bg: 'bg-red-50 dark:bg-red-950/30', text: 'text-red-700 dark:text-red-400', border: 'border-red-200 dark:border-red-800' },
  high: { bg: 'bg-orange-50 dark:bg-orange-950/30', text: 'text-orange-700 dark:text-orange-400', border: 'border-orange-200 dark:border-orange-800' },
  medium: { bg: 'bg-yellow-50 dark:bg-yellow-950/30', text: 'text-yellow-700 dark:text-yellow-400', border: 'border-yellow-200 dark:border-yellow-800' },
  low: { bg: 'bg-green-50 dark:bg-green-950/30', text: 'text-green-700 dark:text-green-400', border: 'border-green-200 dark:border-green-800' },
};

export const SEVERITY_BADGE_STYLES: Record<string, string> = {
  critical: 'bg-red-100 text-red-700 dark:bg-red-950/30 dark:text-red-400',
  high: 'bg-orange-100 text-orange-700 dark:bg-orange-950/30 dark:text-orange-400',
  medium: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-950/30 dark:text-yellow-400',
  low: 'bg-green-100 text-green-700 dark:bg-green-950/30 dark:text-green-400',
};

export const STATUS_BADGE_STYLES: Record<string, string> = {
  pending: 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300',
  assigned: 'bg-blue-50 text-blue-700 dark:bg-blue-950/30 dark:text-blue-400',
  in_progress: 'bg-blue-50 text-blue-700 dark:bg-blue-950/30 dark:text-blue-400',
  blocked: 'bg-orange-50 text-orange-700 dark:bg-orange-950/30 dark:text-orange-400',
  completed: 'bg-green-50 text-green-700 dark:bg-green-950/30 dark:text-green-400',
  cancelled: 'bg-gray-50 text-gray-500 dark:bg-gray-800 dark:text-gray-500',
  open: 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300',
  acknowledged: 'bg-yellow-50 text-yellow-700 dark:bg-yellow-950/30 dark:text-yellow-400',
  resolved: 'bg-green-50 text-green-700 dark:bg-green-950/30 dark:text-green-400',
  closed: 'bg-gray-50 text-gray-500 dark:bg-gray-800 dark:text-gray-500',
  active: 'bg-blue-50 text-blue-700 dark:bg-blue-950/30 dark:text-blue-400',
};

export const PRIORITY_BADGE_STYLES: Record<number, { label: string; cls: string }> = {
  1: { label: 'P1', cls: 'bg-red-100 text-red-700 dark:bg-red-950/30 dark:text-red-400' },
  2: { label: 'P2', cls: 'bg-orange-100 text-orange-700 dark:bg-orange-950/30 dark:text-orange-400' },
  3: { label: 'P3', cls: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-950/30 dark:text-yellow-400' },
  4: { label: 'P4', cls: 'bg-blue-100 text-blue-700 dark:bg-blue-950/30 dark:text-blue-400' },
  5: { label: 'P5', cls: 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400' },
};

export const EVENT_TYPES: { value: EventType | ''; label: string }[] = [
  { value: '', label: 'All Types' },
  { value: 'outage', label: 'Outage' },
  { value: 'maintenance', label: 'Maintenance' },
  { value: 'inspection', label: 'Inspection' },
  { value: 'incident', label: 'Incident' },
  { value: 'weather', label: 'Weather' },
  { value: 'warranty', label: 'Warranty' },
  { value: 'pest', label: 'Pest' },
  { value: 'vegetation', label: 'Vegetation' },
];

export const EVENT_TYPE_LABELS: Record<EventType, string> = {
  outage: 'Outage',
  maintenance: 'Maintenance',
  inspection: 'Inspection',
  incident: 'Incident',
  weather: 'Weather',
  warranty: 'Warranty',
  pest: 'Pest',
  vegetation: 'Vegetation',
};

export const SEVERITIES: { value: SeverityLevel | ''; label: string }[] = [
  { value: '', label: 'All Severities' },
  { value: 'critical', label: 'Critical' },
  { value: 'high', label: 'High' },
  { value: 'medium', label: 'Medium' },
  { value: 'low', label: 'Low' },
];

export const EVENT_STATUSES: { value: EventStatus | ''; label: string }[] = [
  { value: '', label: 'All Statuses' },
  { value: 'open', label: 'Open' },
  { value: 'acknowledged', label: 'Acknowledged' },
  { value: 'in_progress', label: 'In Progress' },
  { value: 'resolved', label: 'Resolved' },
  { value: 'closed', label: 'Closed' },
];

export const TASK_STATUSES: { value: TaskStatus | ''; label: string }[] = [
  { value: '', label: 'All Status' },
  { value: 'pending', label: 'Pending' },
  { value: 'assigned', label: 'Assigned' },
  { value: 'in_progress', label: 'In Progress' },
  { value: 'completed', label: 'Completed' },
  { value: 'blocked', label: 'Blocked' },
  { value: 'cancelled', label: 'Cancelled' },
];

const COMPONENT_CHIP_BASE = 'inline-flex items-center gap-1 px-2 py-0.5 text-[11px] font-medium rounded-md border transition-all cursor-pointer';
const COMPONENT_CHIP_ACTIVE = 'bg-[#17c480]/15 text-[#17c480] border-[#17c480]/40 shadow-sm shadow-[#17c480]/10';
const COMPONENT_CHIP_INACTIVE = 'bg-[var(--color-bg-tertiary)] text-[var(--color-text-secondary)] border-[var(--color-border)] hover:border-[#17c480]/40 hover:text-[#17c480] hover:bg-[#17c480]/5';

export function componentChipClass(isActive: boolean) {
  return `${COMPONENT_CHIP_BASE} ${isActive ? COMPONENT_CHIP_ACTIVE : COMPONENT_CHIP_INACTIVE}`;
}

export const GROUP_STATUS_STYLES: Record<string, string> = {
  active: 'bg-blue-50 text-blue-700 dark:bg-blue-950/30 dark:text-blue-400',
  completed: 'bg-green-50 text-green-700 dark:bg-green-950/30 dark:text-green-400',
  cancelled: 'bg-red-50 text-red-600 dark:bg-red-950/30 dark:text-red-400',
};

export const EVENT_STATUS_HEX_COLORS: Record<string, string> = {
  open: '#9CA3AF',
  acknowledged: '#FBBF24',
  in_progress: '#3B82F6',
  resolved: '#22C55E',
  closed: '#6B7280',
};

export const TASK_STATUS_HEX_COLORS: Record<string, string> = {
  pending: '#9CA3AF',
  assigned: '#60A5FA',
  in_progress: '#3B82F6',
  blocked: '#F97316',
  completed: '#22C55E',
  cancelled: '#6B7280',
};

export const PRIORITY_HEX_COLORS: Record<string, string> = {
  '1': '#EF4444',
  '2': '#F97316',
  '3': '#F59E0B',
  '4': '#3B82F6',
  '5': '#9CA3AF',
};

export const EVENT_TYPE_HEX_COLORS: Record<string, string> = {
  outage: '#EF4444',
  maintenance: '#3B82F6',
  inspection: '#8B5CF6',
  incident: '#F97316',
  weather: '#06B6D4',
  warranty: '#EC4899',
  pest: '#84CC16',
  vegetation: '#22C55E',
};

export const PRIORITY_LABELS: Record<string, string> = {
  '1': 'P1 Critical',
  '2': 'P2 High',
  '3': 'P3 Medium',
  '4': 'P4 Low',
  '5': 'P5 Info',
};

// --- Event Category styles ---

import type { EventCategory } from './eventCategories';

export const EVENT_CATEGORY_HEX_COLORS: Record<EventCategory, string> = {
  preventative_maintenance: '#3B82F6',
  corrective_maintenance: '#EF4444',
  asset_update: '#8B5CF6',
  upcoming: '#F59E0B',
  other: '#6B7280',
};

export const EVENT_CATEGORY_LABELS: Record<EventCategory, string> = {
  preventative_maintenance: 'PM',
  corrective_maintenance: 'CM',
  asset_update: 'Asset Update',
  upcoming: 'Upcoming',
  other: 'Other',
};

export const EVENT_CATEGORY_BADGE_STYLES: Record<EventCategory, string> = {
  preventative_maintenance: 'bg-blue-100 text-blue-700 dark:bg-blue-950/30 dark:text-blue-400',
  corrective_maintenance: 'bg-red-100 text-red-700 dark:bg-red-950/30 dark:text-red-400',
  asset_update: 'bg-purple-100 text-purple-700 dark:bg-purple-950/30 dark:text-purple-400',
  upcoming: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-950/30 dark:text-yellow-400',
  other: 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300',
};
